/** @flow */

import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { localize } from "redux-i18n";
import moment from "moment";
import config from "@config";
import { Button, ComboBox } from "@components";
import type { OptionType } from "@components/combobox";
import type { ShiftPatternDay } from "@modules/employee/reducer/employment-details/types.js";
import { getRowColor } from "@modules/shared/utils/shiftPatternRowColor";
import type { EmployeeAbsenceShiftPattern } from "@modules/employee/reducer/employment-details/types";
import type { EmployeeAbsence } from "@modules/shared/reducer/employee-absences/types";
import classNames from "classnames";

type Props = {
  onChange: (data: Array<EmployeeAbsenceShiftPattern>) => void,
  onValidate: (data: boolean) => void,
  absence: EmployeeAbsence,
  employeePositions: Array<{ value: number, label: string }>,
  shiftPatternDaysConfig: ShiftPatternDay[], // this is comming from the BE and we need to enrich it and render it
  timeLostShiftPattern: EmployeeAbsenceShiftPattern[], // if this is NOT provided then we need to generate pattern based on shiftPatternDaysConfig
  bankHolidays: Array<string>,
  captureBankHolidays: boolean,
  showRepeatButton?: ?boolean,
  minimumAbsenceDuration: number,
  ...PropsFromLocalize,
};

function ShiftPatternTable(props: Props) {
  const absence = props.absence;

  const [shiftPatternDaysToRepeat, setShiftPatternDaysToRepeat]: [
    ShiftPatternDay[],
    Function,
  ] = useState(props.shiftPatternDaysConfig);

  const [bankHolidays, setBankHolidays]: [Array<string>, Function] = useState(props.bankHolidays);

  const [timeLostShiftPattern, setTimeLostShiftPattern]: [
    Array<EmployeeAbsenceShiftPattern>,
    Function,
  ] = useState([]);

  const [timeLostChanged, setTimeLostChanged]: [number, Function] = useState(0);

  useEffect(() => {
    const startDate = moment(absence.startsAt);
    const rtwDate = moment(absence.rtwAt);
    const timeLostSP = props.timeLostShiftPattern; // since its starting from 0

    if (!timeLostSP || !timeLostSP.length) {
      //We don't have any recorded data, so we generate a fresh data
      const newTLSP = makeTimeLostShiftPattern(startDate, rtwDate, shiftPatternDaysToRepeat);
      setTimeLostShiftPattern(newTLSP);
    } else {
      const tlStartDate = moment(timeLostSP[0].recordedDay);
      const tlEndDate = moment(timeLostSP[timeLostSP.length - 1].recordedDay);

      // calculate absence duration in days
      // the "diff" method under, takes into account the hours and minutes from the day too
      // (2024-07-22T00:00:00.000+03:00 - 2024-07-25T00:00:00.000+03:00)
      // (2024/07/22 - 2024/07/25) where both have hours,minutes,seconds set to 00:00:00, will return 3 days
      // (2024-07-22T00:01:00.000+03:00 - 2024-07-25T00:00:00.000+03:00)
      // but if any time (h:m:s) is defined, it will return different day of 3(48:59:00 - round to 2 days)
      // we need to exclude the time from the calculations, use only the dates
      const _startDate = moment(startDate.format("YYYY-MM-DD"));
      const _rtwDate = moment(rtwDate.format("YYYY-MM-DD"));
      const absenceDuration = _rtwDate.diff(_startDate, "days");

      if (
        absenceDuration !== timeLostSP.length - 1 ||
        startDate.diff(tlStartDate, "days") !== 0 ||
        rtwDate.diff(tlEndDate, "days") > 0
      ) {
        // We have some kind of difference between the recorded data and the current absence
        // so we neet to regenerate the missing days (or to remove the extra days
        const initialDays = makeTimeLostShiftPattern(startDate, rtwDate, shiftPatternDaysToRepeat);

        const newTLSP = initialDays.map((day: EmployeeAbsenceShiftPattern) => {
          // day: EmployeeAbsenceShiftPattern is the newly generated day based on the shift pattern
          // Lets check if this day is already recorded in time lost list
          let absenceTLSPday = timeLostSP.find(tlsp => tlsp.recordedDay === day.recordedDay);
          let newDay = absenceTLSPday || day;
          // if this is not the last day of absence, might being the old last day of absence
          // timeLostInSeconds should equal to the workingTimeInSeconds
          // the timeLostInSeconds of the last day of the absence already was generated properly
          // and applied shift pattern configuration in initialDays array
          let _rtwAtDate = rtwDate.format("YYYY-MM-DD");
          if (newDay.recordedDay !== _rtwAtDate) {
            newDay = {
              ...newDay,
              timeLostInSeconds: newDay.workingTimeInSeconds,
            };
          }

          return prepareTheDay(newDay);
        });
        setTimeLostShiftPattern(newTLSP);
      } else {
        setTimeLostShiftPattern(props.timeLostShiftPattern);
      }
    }
  }, [props.timeLostShiftPattern]);

  useEffect(() => {
    if (!timeLostShiftPattern.length) return;
    props.onChange && props.onChange(timeLostShiftPattern);
    props.onValidate && props.onValidate(validateData(timeLostShiftPattern));
  }, [timeLostShiftPattern]);

  function makeTimeLostShiftPattern(
    startsAt: moment$Moment,
    rtwAt: moment$Moment,
    daysToRepeat: ShiftPatternDay[],
  ) {
    const dateArray = [];
    let currentDate = startsAt.startOf("date");
    let rtwAtDate = rtwAt.endOf("date");

    while (currentDate.valueOf() <= rtwAtDate.valueOf()) {
      const day = currentDate.format("ddd").toUpperCase();
      const shiftPatternDay = (daysToRepeat && daysToRepeat.find(spd => spd.day === day)) || null;

      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6;
      // TODO Use day type endpoint instead of hardcoded values beneath
      // day types could be different by region and country
      // Working day - 1
      // Non-working - 2
      let dayTypeId = isWeekend ? 2 : 1;
      let workingTimeInSeconds = 0;
      let timeLostInSeconds = 0;

      if (shiftPatternDay) {
        dayTypeId = shiftPatternDay.isWorkingDay ? 1 : 2;
        workingTimeInSeconds = shiftPatternDay.shiftDurationSec;
        timeLostInSeconds = shiftPatternDay.shiftDurationSec;
      }

      if (currentDate.format("DD/MM/YYYY") === rtwAtDate.format("DD/MM/YYYY")) {
        timeLostInSeconds = 0;
      }

      dateArray.push(
        prepareTheDay({
          dayType: {
            id: dayTypeId,
            name: props.t(dayTypeId === 2 ? "NON_WORKING" : "WORKING"),
            isWorkingDay: dayTypeId !== 2,
          },
          day,
          employeePosition: {
            id: props.employeePositions[0]?.value,
          },
          recordedDay: currentDate.format("YYYY-MM-DD"),
          workingTimeInSeconds: workingTimeInSeconds,
          timeLostInSeconds: timeLostInSeconds,
        }),
      );
      currentDate.add(1, "days");
    }

    return dateArray;
  }

  function getAbsenceDuration() {
    if (!props.absence || !props.absence.rtwAt || !props.absence.startsAt) return 0;
    return moment(props.absence.rtwAt).diff(props.absence.startsAt, "days") + 1;
  }

  // adding the bank holidays to every day which needs
  function prepareTheDay(
    day: EmployeeAbsenceShiftPattern,
    dayToCopyFrom?: EmployeeAbsenceShiftPattern,
  ) {
    if (enableIsCapturedBankHoliday(day.recordedDay)) {
      // set this day to a bank-holiday settings
      return {
        ...day,
        dayType: {
          id: 2,
          isWorkingDay: false,
          name: props.t("NON_WORKING"),
        },
        workingTimeInSeconds: 0,
        timeLostInSeconds: 0,
        recordedDay: day.recordedDay,
      };
    }
    if (!dayToCopyFrom) dayToCopyFrom = day; // If we just generate sp
    return { ...dayToCopyFrom, recordedDay: day.recordedDay, day: day.day };
  }

  function validateData(data: Object[]) {
    const filter = data.map((item, indx) => {
      const isWorkingDay = item.dayType && item.dayType.id === 1;

      // validate if it's a working day and it's not the last day of absence
      if (isWorkingDay && indx < data.length - 1) {
        return (
          item.hasOwnProperty("workingTimeInSeconds") &&
          typeof item.workingTimeInSeconds === "number" &&
          item.workingTimeInSeconds > 0 &&
          item.hasOwnProperty("timeLostInSeconds") &&
          typeof item.timeLostInSeconds === "number" &&
          item.timeLostInSeconds > 0 &&
          item.timeLostInSeconds <= item.workingTimeInSeconds
        );
      }

      return true;
    });

    const isPatternsValid = !filter.includes(false);
    const isPositionValid = data.every(r => (r.dayType.id == 1 ? !!r.employeePosition?.id : true));
    const isTimeLostValid =
      data.reduce((acc, cv) => acc + cv.timeLostInSeconds, 0) >= props.minimumAbsenceDuration;

    return isPatternsValid && isPositionValid && isTimeLostValid;
  }

  function isBankHoliday(date: string) {
    return bankHolidays.indexOf(new Date(date).toISOString().substring(0, 10)) !== -1;
  }

  //#region handleRepeat
  function handleRepeat(daysN: number) {
    return function() {
      const daysToRepeat = timeLostShiftPattern.slice(0, daysN);
      const newShiftPattern = timeLostShiftPattern.map(
        (day: EmployeeAbsenceShiftPattern, idx: number) => {
          const n = idx >= daysToRepeat.length ? idx % daysN : idx;
          let _timeLostInSeconds = daysToRepeat[n].timeLostInSeconds;

          // the last the of absence timeLostInSeconds is = 0
          if (idx === timeLostShiftPattern.length - 1) {
            _timeLostInSeconds = 0;
          } else {
            // in case that user extends the absence duration
            // the day that was last before needs to resume it's timeLostInSeconds to be as working hours for the day
            // timeLostInSeconds === workingTimeInSeconds
            // that way only the last day of the absence will have no time lost timeLostInSeconds === 0
            _timeLostInSeconds =
              _timeLostInSeconds === 0
                ? daysToRepeat[n].workingTimeInSeconds
                : daysToRepeat[n].timeLostInSeconds;
          }
          const dayToRepeat = {
            ...daysToRepeat[n],
            timeLostInSeconds: _timeLostInSeconds,
          };

          return prepareTheDay(day, dayToRepeat);
        },
      );

      handleChange(newShiftPattern);
    };
  }

  //#region handleChange
  function handleChange(change: any) {
    setTimeLostChanged(timeLostChanged + 1);
    setTimeLostShiftPattern(change);
  }

  function generateHourMinutesOptions(limit: number): Array<OptionType> {
    return [...new Array(limit)].map((_: *, i: number) => ({
      label: `${i.toString().padStart(2, "0")}`,
      value: i,
    }));
  }

  function secondsToHoursSeconds(seconds) {
    const hours = Math.floor(seconds / 3600);
    const remainingSeconds = Math.floor((seconds - hours * 3600) / 60);
    return [hours, remainingSeconds];
  }

  function updateWorkingHours(rowIdx: number, value: number) {
    const workingTimeInSeconds =
      value * 60 * 60 +
      secondsToHoursSeconds(timeLostShiftPattern[rowIdx].workingTimeInSeconds)[1] * 60;
    const timeLostInSeconds: number = workingTimeInSeconds;
    const newTimeLostShiftPattern = timeLostShiftPattern.map(
      (shift: EmployeeAbsenceShiftPattern, idx: number) => {
        if (rowIdx !== idx) return shift;
        else if (rowIdx === idx) {
          return {
            ...shift,
            workingTimeInSeconds,
            // if this is the last day of the absence timeLostInSeconds should be 0
            timeLostInSeconds: rowIdx === timeLostShiftPattern.length - 1 ? 0 : timeLostInSeconds,
          };
        }
      },
    );

    handleChange(newTimeLostShiftPattern);
  }

  function updateWorkingMinutes(rowIdx: number, value: number) {
    const newTimeLostShiftPattern = timeLostShiftPattern.map(
      (shift: EmployeeAbsenceShiftPattern, idx: number) => {
        if (rowIdx !== idx) return shift;
        else if (rowIdx === idx)
          return {
            ...shift,
            workingTimeInSeconds:
              value * 60 +
              secondsToHoursSeconds(timeLostShiftPattern[rowIdx].workingTimeInSeconds)[0] * 60 * 60,
            // Auto-Update the lost minutes if their value is not already set
            // if this is the last day of the absence timeLostInSeconds should be 0
            timeLostInSeconds:
              rowIdx === timeLostShiftPattern.length - 1
                ? 0
                : value * 60 +
                  secondsToHoursSeconds(timeLostShiftPattern[rowIdx].timeLostInSeconds)[0] *
                    60 *
                    60,
          };
      },
    );
    handleChange(newTimeLostShiftPattern);
  }

  function updateLostHours(rowIdx: number, value: number) {
    const newTimeLostShiftPattern: Array<EmployeeAbsenceShiftPattern> = timeLostShiftPattern.map(
      //$FlowIgnore
      (shift: EmployeeAbsenceShiftPattern, idx: number) => {
        if (rowIdx !== idx) {
          return shift;
        } else if (rowIdx === idx) {
          const timeLostInSeconds =
            value * 60 * 60 +
            secondsToHoursSeconds(timeLostShiftPattern[rowIdx].timeLostInSeconds)[1] * 60;

          return {
            ...shift,
            timeLostInSeconds:
              timeLostInSeconds > shift.workingTimeInSeconds
                ? shift.workingTimeInSeconds
                : timeLostInSeconds,
          };
        }
      },
    );
    handleChange(newTimeLostShiftPattern);
  }

  function updateLostMinutes(rowIdx: number, value: number) {
    const newTimeLostShiftPattern = timeLostShiftPattern.map(
      (shift: EmployeeAbsenceShiftPattern, idx: number) => {
        if (rowIdx !== idx) {
          return shift;
        } else if (rowIdx === idx) {
          const timeLostInSeconds =
            value * 60 +
            secondsToHoursSeconds(timeLostShiftPattern[rowIdx].timeLostInSeconds)[0] * 60 * 60;

          return {
            ...shift,
            timeLostInSeconds:
              timeLostInSeconds > shift.workingTimeInSeconds
                ? shift.workingTimeInSeconds
                : timeLostInSeconds,
          };
        }
      },
    );
    handleChange(newTimeLostShiftPattern);
  }

  function getRowBackground(date: string) {
    "background-color-weekend";
    "background-color-holiday";
  }

  function enableIsCapturedBankHoliday(recordedDay: string): boolean {
    return isBankHoliday(recordedDay) && props.captureBankHolidays === false;
  }

  function isNonWorkingDay(day: EmployeeAbsenceShiftPattern): boolean {
    // 1 = working day
    // 2 = non-working day
    return day?.dayType?.id === 2;
  }

  //#region render
  return (
    <React.Fragment>
      {props.showRepeatButton && (
        <div className="d-flex justify-content-end">
          <h6 className="mr-2 mt-2">{props.t("REPEAT_SHIFT_PATTERN_TITLE_DAYS")}: </h6>
          <Button className="mr-1" onClick={handleRepeat(6)}>
            {props.t("REPEAT_SHIFT_PATTERN_DAYS", { days: 6 })}
          </Button>
          <Button className="mr-1" onClick={handleRepeat(7)}>
            {props.t("REPEAT_SHIFT_PATTERN_DAYS", { days: 7 })}
          </Button>
          <Button className="mr-1" onClick={handleRepeat(8)}>
            {props.t("REPEAT_SHIFT_PATTERN_DAYS", { days: 8 })}
          </Button>
          <Button className="mr-1" onClick={handleRepeat(10)}>
            {props.t("REPEAT_SHIFT_PATTERN_DAYS", { days: 10 })}
          </Button>
          <Button onClick={handleRepeat(14)}>{props.t("14 Days")}</Button>
        </div>
      )}
      <div className={"shift-patterns-table"}>
        <div className="shift-patterns-table-row">
          <span className="shift-patterns-table-cell w-flex-100px">{props.t("DATE")}</span>
          <span className="shift-patterns-table-cell  w-flex-50px">{props.t("DAY")}</span>
          <span className="shift-patterns-table-cell">{props.t("DAY_TYPE")}</span>
          <span className="shift-patterns-table-cell">{props.t("ROLE")}</span>
          <span className="shift-patterns-table-cell">{props.t("HOURS_DUE_TO_WORK")}</span>
          <span className="shift-patterns-table-cell">{props.t("HOURS_LOST")}</span>
        </div>
        {timeLostShiftPattern.map((day, rowIndex) => {
          // the validation fails when time lost = 0, it's not the last day of absence, is working day
          let timeLostErrorClass =
            rowIndex < timeLostShiftPattern.length - 1 &&
            day.timeLostInSeconds === 0 &&
            day.dayType.id === 1;

          // the validation fails when working time = 0, it's not the last day of absence, is working day
          let workingTimeErrorClass =
            rowIndex < timeLostShiftPattern.length - 1 &&
            day.workingTimeInSeconds === 0 &&
            day.dayType.id === 1;

          return (
            <div
              className={`shift-patterns-table-row ${getRowColor(bankHolidays)(day, rowIndex)}`}
              key={day.recordedDay}
            >
              <span className="shift-patterns-table-cell  w-flex-100px">
                {moment(day.recordedDay).format(config.dateFormat)}
              </span>
              <span className="shift-patterns-table-cell  w-flex-50px">
                {day.day
                  .charAt(0)
                  .toUpperCase()
                  .concat(day.day.toLowerCase().substr(1, 2))}
              </span>
              <span className="shift-patterns-table-cell">
                <ComboBox
                  className="w-190-px"
                  options={[
                    { value: 1, label: "Working Day" },
                    { value: 2, label: "Non-Working Day" },
                  ]}
                  value={day.dayType.id}
                  onChange={val => {
                    handleChange(
                      timeLostShiftPattern.map((row: *, rIndex: number) => {
                        return rIndex === rowIndex
                          ? { ...row, dayType: { id: val, name: row.dayType.name } }
                          : row;
                      }),
                    );
                  }}
                  disabled={enableIsCapturedBankHoliday(day.recordedDay)}
                />
              </span>
              <span className="shift-patterns-table-cell">
                <ComboBox
                  className="w-100"
                  options={props.employeePositions}
                  value={day?.employeePosition?.id}
                  onChange={val => {
                    handleChange(
                      timeLostShiftPattern.map((row: *, rIndex: number) => {
                        return rIndex === rowIndex
                          ? { ...row, employeePosition: { id: val } }
                          : row;
                      }),
                    );
                  }}
                  disabled={enableIsCapturedBankHoliday(day.recordedDay) || isNonWorkingDay(day)}
                />
              </span>
              <span className="shift-patterns-table-cell">
                <ComboBox
                  id={`working-hours-${day.recordedDay}`}
                  className={classNames("mr-1", {
                    __error: workingTimeErrorClass,
                  })}
                  options={generateHourMinutesOptions(24)}
                  value={
                    day.dayType.id === 2 ? 0 : secondsToHoursSeconds(day.workingTimeInSeconds)[0]
                  }
                  onChange={val => updateWorkingHours(rowIndex, val)}
                  disabled={enableIsCapturedBankHoliday(day.recordedDay) || isNonWorkingDay(day)}
                />
                <ComboBox
                  id={`working-minutes-${day.recordedDay}`}
                  className={classNames({
                    __error: workingTimeErrorClass,
                  })}
                  options={generateHourMinutesOptions(60)}
                  value={
                    day.dayType.id === 2 ? 0 : secondsToHoursSeconds(day.workingTimeInSeconds)[1]
                  }
                  onChange={val => updateWorkingMinutes(rowIndex, val)}
                  disabled={enableIsCapturedBankHoliday(day.recordedDay) || isNonWorkingDay(day)}
                />
              </span>

              <span className="shift-patterns-table-cell">
                <ComboBox
                  id={`lost-hours-${day.recordedDay}`}
                  className={classNames("mr-1", {
                    __error: timeLostErrorClass,
                  })}
                  options={generateHourMinutesOptions(24)}
                  value={day.dayType.id === 2 ? 0 : secondsToHoursSeconds(day.timeLostInSeconds)[0]}
                  onChange={val => updateLostHours(rowIndex, val)}
                  disabled={
                    enableIsCapturedBankHoliday(day.recordedDay) ||
                    isNonWorkingDay(day) ||
                    (!isNonWorkingDay(day) && day.workingTimeInSeconds === 0)
                  }
                />
                <ComboBox
                  id={`lost-minutes-${day.recordedDay}`}
                  className={classNames({
                    __error: timeLostErrorClass,
                  })}
                  options={generateHourMinutesOptions(60)}
                  value={day.dayType.id === 2 ? 0 : secondsToHoursSeconds(day.timeLostInSeconds)[1]}
                  onChange={val => updateLostMinutes(rowIndex, val)}
                  disabled={
                    enableIsCapturedBankHoliday(day.recordedDay) ||
                    isNonWorkingDay(day) ||
                    (!isNonWorkingDay(day) && day.workingTimeInSeconds === 0)
                  }
                />
              </span>
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
}

export default compose(localize())(ShiftPatternTable);
